import { Accessor, Show, Suspense } from "solid-js";
import { createAsync, query } from "@solidjs/router";
import { BrandHeader } from "./fragments/brand_header";
import { WhatsappSharingInfo } from "~/components/webstore_telescope/whatsapp_sharing_info";
import { AboutBrand } from "~/components/webstore/about_brand";
import { VoucherProperties } from "~/components/webstore/voucher_properties";
import { ActionsList } from "~/components/webstore_telescope/actions_list";
import { NearbyStoreMap } from "~/components/webstore_telescope/nearby_store_map";
import { HubbleBadge } from "~/components/webstore_telescope/hubble_badge";
import { clientRepo } from "~/server/apis/client_repo";
import { useWebStoreContext } from "~/context/webstore_context";
import { getVoucherDescBrandingReplacement } from "~/data/partner_config";
import { DenominationsGridV2 } from "~/components/webstore_telescope/denominations_grid_v2/denominations_grid_v2";
import {
  BrandL2TelescopeRouteData,
  getBrandL2TelescopeRouteData,
} from "./data/brand_l2_telescope_route_data";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import { useRnrBrandPage } from "./hooks/use_rnr_brand_page";

const getBrandL2RouteData$C = query(
  getBrandL2TelescopeRouteData,
  "brand-l2-telescope-client"
);

export type BrandL2TelescopeViewProps = {
  brandKey: string;
};

/**
 * Telescope Design System version of Brand L2 page (gift card purchase page)
 * Mobile design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=308-14704
 * Desktop design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=106-492
 */
export function BrandL2TelescopeView(props: BrandL2TelescopeViewProps) {
  const routeData = createAsync(() => getBrandL2RouteData$C(props.brandKey), {
    deferStream: true,
  });

  return (
    <Suspense fallback={<FullScreenLoaderTelescope />}>
      <Show when={routeData()}>
        <_BrandL2TelescopeViewContent
          brandKey={props.brandKey}
          routeData={routeData as Accessor<BrandL2TelescopeRouteData>}
        />
      </Show>
    </Suspense>
  );
}

function _BrandL2TelescopeViewContent(props: {
  brandKey: string;
  routeData: Accessor<BrandL2TelescopeRouteData>;
}) {
  const brandPageData = useRnrBrandPage({
    brandKey: props.brandKey,
    routeData: props.routeData,
  });

  const { routeData, onClickGetThisCard, ctaState, setCtaState } =
    brandPageData;

  const {
    storeState,
    getPartnerConfigV3,
    openContactSupport,
    getMode,
    getClientId,
  } = useWebStoreContext();

  function buildDenominationGrid() {
    return (
      <DenominationsGridV2
        initialVariantId={
          routeData()?.brand.variantDetailedResponse?.find(
            (variant) => variant.isDefault
          )?.variantId ?? null
        }
        initialConsumeCoins={true}
        brand={routeData()!.brand}
        isCoinsOnly={
          routeData()?.brand.restrictions.paymentSource === "COINS_ONLY"
        }
        allowRewardCoins={false}
        showHubbleCoinRewards={false}
        coinsBalance={storeState.coins ?? 0}
        isRewardTypePostPaid={routeData()!.brand.rewardType === "POSTPAID"}
        simulateFunction={(params) => clientRepo.simulate(params)}
        initiateOrder={(props) => onClickGetThisCard(props)}
        ctaState={ctaState}
        setCtaState={setCtaState}
      />
    );
  }

  return (
    <div class="flex min-h-screen w-full flex-col items-center bg-background-normal-primary">
      <div class="w-full  px-3 py-3 sm:max-w-[360px]  sm:px-0">
        <div class="flex flex-col">
          {/* Brand Header with Back Button */}
          <BrandHeader
            brandName={routeData()!.brand.voucherProductMetadata.title}
          />

          {/* GC Amount Box (Denomination Grid) */}
          <div class="mt-3 overflow-hidden rounded-[16px] bg-background-mapping-reverse p-1">
            {buildDenominationGrid()}
          </div>

          {/* Main Content */}
          <div class="mt-6 flex flex-col gap-6 px-1">
            {/* WhatsApp Sharing Info */}
            <Show when={getPartnerConfigV3().showWhatsappDeliveryMessage}>
              <WhatsappSharingInfo />
            </Show>

            {/* Voucher Properties */}
            <VoucherProperties
              voucherHighlights={
                routeData()!.brandExtraMetadata.voucherHighlights
              }
            />

            {/* Actions List */}
            <ActionsList
              brandId={routeData()!.brand.externalId}
              productId={routeData()!.brand.externalId}
              onContactSupport={openContactSupport}
              showContactSupport={getPartnerConfigV3().showContactSupport}
            />

            {/* Nearby Store Map */}
            <Show
              when={routeData()?.brand.voucherProductMetadata.storeLocatorUrl}
            >
              <NearbyStoreMap
                storeLocatorUrl={
                  routeData()!.brand.voucherProductMetadata.storeLocatorUrl!
                }
              />
            </Show>

            {/* About the Brand */}
            <Show when={routeData()!.brandExtraMetadata.brandDescription}>
              <AboutBrand
                brandName={routeData()!.brand.voucherProductMetadata.title}
                brandDescription={routeData()!.brandExtraMetadata.brandDescription!.replaceAll(
                  "Hubble",
                  getVoucherDescBrandingReplacement(getClientId())
                )}
                brandLogoUrl={
                  routeData()!.brand.voucherProductMetadata.logoUrl ?? ""
                }
              />
            </Show>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Show when={getPartnerConfigV3().showHubbleBranding}>
        <div class="mt-8"></div>
        <div class="fixed bottom-0  flex w-full flex-col gap-2 bg-background-normal-primary px-3 pb-2">
          <div class="h-px w-full bg-stroke-1" />
          <div class="flex w-full flex-col items-center">
            <HubbleBadge />
          </div>
        </div>
      </Show>
    </div>
  );
}
